import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import { Field } from '@base-ui/react/field';
import { CaretDownIcon, PlusIcon, WarningIcon, XIcon, XCircleIcon } from '@phosphor-icons/react';
import React, { useCallback, useId, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Checkbox from './Checkbox';
import { InputFieldProps } from './Input';
import { popupPlacement, type PopupPlacement } from './popupPlacement';

export interface ComboboxOption {
  id?: string | number;
  label: string;
  value: string;
  disabled?: boolean;
  hint?: string;
  /** Shown before the label, such as an avatar. */
  icon?: React.ReactNode;
}

/** A row pinned to the foot of the menu whatever the query, such as "Add someone new". */
export interface ComboboxAction {
  label: string;
  onSelect: () => void;
}

const ACTION_VALUE = '__combobox-action__';

type ListItem = {
  type: 'freetext' | 'selected-freetext' | 'option';
  label: string;
  value: string;
  isValid: boolean;
  disabled?: boolean;
  hint?: string;
  icon?: React.ReactNode;
};

/**
 * Extracts a display name from an email address or partial email.
 * Examples:
 *   "joh" -> "Joh"
 *   "john.s" -> "John S"
 *   "john.smith" -> "John Smith"
 *   "john.smith1@nhs.net" -> "John Smith"
 */
export function extractNameFromEmail(input: string): string {
  if (!input) return '';
  const localPart = input.split('@')[0];
  const processedParts = localPart.split('.').map((part) => {
    const withoutNumbers = part.replace(/\d+$/, '');
    if (!withoutNumbers) return '';
    return withoutNumbers.charAt(0).toUpperCase() + withoutNumbers.slice(1).toLowerCase();
  });
  return processedParts.filter(Boolean).join(' ');
}

// `onSelect` here is a value callback. Omit the DOM select handler so the two types don't collide.
type FieldComboboxProps = Omit<InputFieldProps, 'onSelect'> & {
  variant: 'field';
  options: Array<string | ComboboxOption>;
  emptyText?: string;
  menuClassName?: string;
  placement?: PopupPlacement;
  maxHeight?: string;
  onSearch?: (query: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  allowCreate?: boolean;
  validateValue?: (value: string) => boolean;
  validationMessage?: string;
  createOptionLabel?: string;
  invalidCreateTitle?: string;
  invalidCreateDescription?: string;
  onSelect?: (value: string) => void;
  action?: ComboboxAction;
};

function toComboboxOptions(options: Array<string | ComboboxOption>): ComboboxOption[] {
  return options.map((option) => (typeof option === 'string' ? { label: option, value: option } : option));
}

const ULID_VALUE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

function isInternalOptionValue(value: string): boolean {
  return ULID_VALUE.test(value) || value.startsWith('domain-default:');
}

function shouldShowOptionValue(label: string, value: string): boolean {
  if (label === value) return false;
  if (isInternalOptionValue(value)) return false;
  return true;
}

/**
 * Email-invite comboboxes pass `validateValue`. An invalid query then explains
 * how to invite someone, instead of offering a disabled "Add" row. Callers
 * can still override the copy.
 */
function resolveEmailInviteCopy(validatesValue: boolean): {
  createOptionLabel: string;
  invalidCreateTitle?: string;
  invalidCreateDescription?: string;
} {
  if (!validatesValue) return { createOptionLabel: 'Add:' };
  return {
    createOptionLabel: 'Invite:',
    invalidCreateTitle: 'No matching users found',
    invalidCreateDescription: 'Enter full email address to invite someone'
  };
}

function optionMatchesQuery(option: ComboboxOption, query: string): boolean {
  if (option.label.toLowerCase().includes(query)) return true;
  if (isInternalOptionValue(option.value)) return false;
  return option.value.toLowerCase().includes(query);
}

type ListComboboxProps = {
  variant?: 'list';
  options?: ComboboxOption[];
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  allowCreate?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  maxHeight?: string;
  validateValue?: (value: string) => boolean;
  validationMessage?: string;
  createOptionLabel?: string;
  invalidCreateTitle?: string;
  invalidCreateDescription?: string;
  ariaLabel?: string;
  id?: string;
  generateLabelFromValue?: (value: string) => string;
  onOpen?: () => void;
  emptyText?: string;
  searchPlaceholder?: string;
  /**
   * How a multi-select trigger shows its selection: removable chips that wrap, or one truncated line of labels
   * at a ``Dropdown``'s height, to sit in a toolbar.
   */
  selectionDisplay?: 'chips' | 'line';
  /** Offer a multi-select row that selects, or clears, every option the search currently lists. */
  enableSelectAll?: boolean;
  /** Shown before the "Select all" label, matching the options' icons. */
  selectAllIcon?: React.ReactNode;
};

export type ComboboxProps = FieldComboboxProps | ListComboboxProps;

type MultiListComboboxProps = Omit<ListComboboxProps, 'multiple' | 'value' | 'onChange'> & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type SingleListComboboxProps = Omit<ListComboboxProps, 'multiple' | 'value' | 'onChange'> & {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
};

const fieldBaseStyle =
  'w-full h-[36px] px-[13px] py-[8px] gap-2 rounded-[6px] text-[14px] font-medium font-raleway leading-[100%]';

function activateRoleButton(event: React.KeyboardEvent, action: (event: React.KeyboardEvent) => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (event.key === ' ') {
    event.preventDefault();
  }
  action(event);
}

const FieldCombobox = React.forwardRef<HTMLInputElement, FieldComboboxProps>(
  (
    {
      options,
      emptyText = 'No options found',
      menuClassName,
      placement = 'bottom-start',
      maxHeight = 'max-h-60',
      onSearch,
      onOpen,
      onClose,
      allowCreate = false,
      validateValue,
      validationMessage,
      createOptionLabel: createOptionLabelProp,
      invalidCreateTitle: invalidCreateTitleProp,
      invalidCreateDescription: invalidCreateDescriptionProp,
      onSelect,
      action,
      state = 'default',
      icon,
      onIconClick,
      errorMessage,
      className,
      fullWidth = false,
      wrapperClassName,
      inputClassName,
      disabled,
      value,
      onChange,
      onFocus,
      onBlur,
      onClick,
      type: _type,
      variant: _variant,
      ...inputProps
    },
    ref
  ) => {
    const emailInvite = resolveEmailInviteCopy(validateValue != null);
    const createOptionLabel = createOptionLabelProp ?? emailInvite.createOptionLabel;
    const invalidCreateTitle = invalidCreateTitleProp ?? emailInvite.invalidCreateTitle;
    const invalidValueMessage = validationMessage ?? 'Enter a valid email address';
    const invalidCreateDescription =
      invalidCreateDescriptionProp ?? validationMessage ?? emailInvite.invalidCreateDescription ?? invalidValueMessage;
    const [fieldAnchor, setFieldAnchor] = useState<HTMLDivElement | null>(null);
    const { side, align } = popupPlacement(placement);
    const currentValue = value == null ? '' : String(value);
    const optionItems = toComboboxOptions(options);
    const query = currentValue.trim().toLowerCase();
    const filteredOptions = query ? optionItems.filter((option) => optionMatchesQuery(option, query)) : optionItems;
    const exactMatch = optionItems.some((option) => option.value.toLowerCase() === query);
    const createOption =
      allowCreate && query && filteredOptions.length === 0 && !exactMatch
        ? {
            label: currentValue.trim(),
            value: currentValue.trim(),
            isCreate: true,
            isValid: validateValue ? validateValue(currentValue.trim()) : true
          }
        : null;
    const hasNoMatches = filteredOptions.length === 0 && !createOption;
    const actionItem = action ? { label: action.label, value: ACTION_VALUE, isAction: true } : null;
    const displayItems = [
      ...(createOption ? [createOption] : []),
      ...filteredOptions,
      ...(actionItem ? [actionItem] : [])
    ];
    const selected = optionItems.find((option) => option.value === currentValue) ?? null;
    const visualState = state === 'error' ? 'error' : 'default';
    const borderColor =
      visualState === 'error' ? 'border-misc-error' : 'border-border-default focus-within:border-border-active';
    const textColor =
      visualState === 'error' ? 'text-content-default' : 'text-content-subtle focus-within:text-content-default';

    const emitChange = (next: string) => {
      onChange?.({
        target: { value: next },
        currentTarget: { value: next }
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <BaseCombobox.Root
        items={displayItems}
        filteredItems={displayItems}
        value={selected}
        inputValue={currentValue}
        itemToStringLabel={(item) => item.label}
        itemToStringValue={(item) => item.value}
        isItemEqualToValue={(itemValue, current) => itemValue.value.toLowerCase() === current.value.toLowerCase()}
        onInputValueChange={(next) => {
          onSearch?.(next);
          emitChange(next);
        }}
        onValueChange={(next) => {
          if (next == null) return;
          if ('isAction' in next) {
            action?.onSelect();
            return;
          }
          if ('isValid' in next && next.isValid === false) return;
          if ('disabled' in next && next.disabled) return;
          if (onSelect) {
            onSelect(next.value);
            return;
          }
          emitChange(next.value);
        }}
        onOpenChange={(open) => {
          if (open) onOpen?.();
          else onClose?.();
        }}
        disabled={disabled}
        modal={false}
        autoHighlight={'always' as unknown as boolean}
      >
        <Field.Root
          invalid={visualState === 'error'}
          disabled={disabled}
          className={twMerge(fullWidth && 'w-full', 'flex flex-col gap-[4px]', className)}
        >
          <div
            ref={setFieldAnchor}
            className={twMerge(
              'flex items-center border-[2px] bg-white',
              fieldBaseStyle,
              wrapperClassName,
              borderColor,
              textColor,
              disabled && 'opacity-60 cursor-not-allowed bg-bluegray-50'
            )}
          >
            <BaseCombobox.Input
              ref={ref}
              disabled={disabled}
              className={twMerge('w-full outline-none bg-transparent', inputClassName)}
              onFocus={onFocus}
              onBlur={onBlur}
              onClick={onClick}
              {...inputProps}
            />
            {icon && (
              <div className={twMerge('text-content-subtlest', onIconClick && 'cursor-pointer')} onClick={onIconClick}>
                {React.createElement(icon, { size: 20, weight: 'regular' })}
              </div>
            )}
          </div>
          {visualState === 'error' && errorMessage && (
            <Field.Error
              match
              className="flex items-center gap-[10px] text-[#FF0000] font-raleway font-normal text-[14px] leading-[120%] tracking-[0] text-center align-middle mt-[3px]"
            >
              <span>
                <WarningIcon size={20} weight="regular" color="#FF0000" />
              </span>
              <span>{errorMessage}</span>
            </Field.Error>
          )}
        </Field.Root>

        <BaseCombobox.Portal>
          <BaseCombobox.Positioner anchor={fieldAnchor} side={side} align={align} sideOffset={8} className="z-critical">
            <BaseCombobox.Popup
              className={twMerge(
                'bg-white border-[2px] border-border-default rounded-lg shadow-md overflow-hidden outline-none w-[var(--anchor-width)]',
                maxHeight,
                menuClassName
              )}
            >
              <div className="overflow-y-auto custom-scrollbar max-h-48">
                <BaseCombobox.Empty
                  className="text-sm text-content-subtlest text-left empty:p-0 px-3 py-2"
                  // Empty renders children only while the list is empty, which the action row prevents,
                  // so the text follows the match count instead; Empty's live region still announces it.
                  render={(props) => <div {...props}>{hasNoMatches && emptyText}</div>}
                />
                <BaseCombobox.List>
                  {(option: ComboboxOption & { isCreate?: boolean; isValid?: boolean; isAction?: boolean }) => {
                    if (option.isAction) {
                      return (
                        <BaseCombobox.Item
                          key="action"
                          value={option}
                          className="sticky bottom-0 flex w-full items-center gap-2 border-t border-border-subtle bg-white px-3 py-2 text-sm font-medium text-secondary-blue outline-none cursor-pointer hover:bg-gray-50 data-[highlighted]:bg-gray-50"
                        >
                          <PlusIcon size={14} aria-hidden="true" />
                          <span className="truncate">{option.label}</span>
                        </BaseCombobox.Item>
                      );
                    }
                    const isInvalid = option.isCreate && option.isValid === false;
                    return (
                      <BaseCombobox.Item
                        key={`${option.isCreate ? 'create' : 'option'}-${option.value}`}
                        value={option}
                        disabled={isInvalid || option.disabled === true}
                        className={twMerge(
                          'flex w-full flex-col px-3 py-2 text-sm text-left outline-none',
                          isInvalid && invalidCreateTitle
                            ? 'cursor-default text-content-subtle'
                            : isInvalid || option.disabled
                              ? 'cursor-not-allowed opacity-60 text-content-subtle'
                              : 'cursor-pointer hover:bg-gray-50 data-[highlighted]:bg-gray-50 data-[selected]:bg-blue-50 data-[selected]:text-blue-900 text-gray-900'
                        )}
                      >
                        {isInvalid && invalidCreateTitle ? (
                          <>
                            <div className="font-medium">{invalidCreateTitle}</div>
                            <div className="text-sm text-content-subtle mt-0.5">{invalidCreateDescription}</div>
                          </>
                        ) : (
                          <>
                            <div
                              className={twMerge(
                                'font-medium truncate',
                                option.icon != null && 'flex items-center gap-2.5'
                              )}
                            >
                              {option.icon}
                              {/* A flex row does not ellipsise its text, so the label truncates itself. */}
                              <span className="truncate">
                                {option.isCreate && (
                                  <span className="text-content-subtle italic mr-1">{createOptionLabel}</span>
                                )}
                                {option.label}
                              </span>
                            </div>
                            {option.hint ? (
                              <div className="text-xs text-content-subtle truncate">{option.hint}</div>
                            ) : (
                              shouldShowOptionValue(option.label, option.value) && (
                                <div className="text-xs text-content-subtle truncate">{option.value}</div>
                              )
                            )}
                            {isInvalid && <div className="text-xs text-red-600 mt-0.5">{invalidValueMessage}</div>}
                          </>
                        )}
                      </BaseCombobox.Item>
                    );
                  }}
                </BaseCombobox.List>
              </div>
            </BaseCombobox.Popup>
          </BaseCombobox.Positioner>
        </BaseCombobox.Portal>
      </BaseCombobox.Root>
    );
  }
);

FieldCombobox.displayName = 'FieldCombobox';

function defaultLabelFromValue(value: string): string {
  return value;
}

function ListCombobox({
  options = [],
  value,
  onChange,
  multiple = false,
  allowCreate = false,
  placeholder = 'Select...',
  disabled = false,
  className,
  size = 'md',
  maxHeight = 'max-h-80',
  validateValue,
  validationMessage,
  createOptionLabel: createOptionLabelProp,
  invalidCreateTitle: invalidCreateTitleProp,
  invalidCreateDescription: invalidCreateDescriptionProp,
  ariaLabel,
  id,
  generateLabelFromValue = defaultLabelFromValue,
  onOpen,
  emptyText = 'No options',
  searchPlaceholder = 'Type to search or enter email...',
  selectionDisplay = 'chips',
  enableSelectAll = false,
  selectAllIcon
}: ListComboboxProps) {
  const emailInvite = resolveEmailInviteCopy(validateValue != null);
  const createOptionLabel = createOptionLabelProp ?? emailInvite.createOptionLabel;
  const invalidCreateTitle = invalidCreateTitleProp ?? emailInvite.invalidCreateTitle;
  const invalidValueMessage = validationMessage ?? 'Enter a valid email address';
  const invalidCreateDescription =
    invalidCreateDescriptionProp ?? validationMessage ?? emailInvite.invalidCreateDescription ?? invalidValueMessage;
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const generatedId = useId();
  const inputId = id || generatedId;
  const listboxId = `${inputId}-listbox`;
  const selectedValues = multiple ? ((value as string[]) ?? []) : [];
  const singleValue = multiple ? null : ((value as string | null) ?? null);

  const sizeClass = {
    sm: 'min-h-[28px] px-2 py-1',
    md: 'min-h-[36px] px-2.5 py-1.5'
  };
  const fontSizeClass = {
    sm: 'text-xs',
    md: 'text-sm'
  };

  const selectedSet = useMemo(() => new Set(selectedValues.map((v) => v.toLowerCase())), [selectedValues]);

  const optionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of options) {
      map.set(opt.value.toLowerCase(), opt.label);
    }
    return map;
  }, [options]);

  const getLabel = useCallback(
    (val: string): string => optionLabelMap.get(val.toLowerCase()) || generateLabelFromValue(val),
    [optionLabelMap, generateLabelFromValue]
  );

  const selectedOption = useMemo(() => {
    if (!singleValue) return null;
    return options.find((o) => o.value.toLowerCase() === singleValue.toLowerCase()) || null;
  }, [singleValue, options]);

  const displayLabel = useMemo(() => {
    if (!singleValue) return null;
    if (selectedOption) return selectedOption.label;
    return generateLabelFromValue(singleValue);
  }, [singleValue, selectedOption, generateLabelFromValue]);

  const filteredOptions = useMemo(() => {
    const query = inputValue.toLowerCase().trim();
    if (!query) return options;
    return options.filter((option) => optionMatchesQuery(option, query));
  }, [options, inputValue]);

  const displayList = useMemo(() => {
    const items: ListItem[] = [];

    if (allowCreate && inputValue.trim() && filteredOptions.length === 0) {
      const exactMatch = options.find((o) => o.value.toLowerCase() === inputValue.toLowerCase().trim());
      if (!exactMatch) {
        const trimmedValue = inputValue.trim();
        items.push({
          type: 'freetext',
          label: generateLabelFromValue(trimmedValue),
          value: trimmedValue,
          isValid: validateValue ? validateValue(trimmedValue) : true
        });
      }
    }

    const query = inputValue.toLowerCase().trim();
    if (multiple) {
      for (const selectedVal of selectedValues) {
        const matchesOption = options.some((o) => o.value.toLowerCase() === selectedVal.toLowerCase());
        if (matchesOption) continue;
        if (inputValue.trim() && inputValue.toLowerCase().trim() === selectedVal.toLowerCase()) continue;
        const selectedLabel = generateLabelFromValue(selectedVal);
        if (query && !selectedLabel.toLowerCase().includes(query) && !selectedVal.toLowerCase().includes(query)) {
          continue;
        }
        items.push({ type: 'selected-freetext', label: selectedLabel, value: selectedVal, isValid: true });
      }
    } else if (singleValue && !selectedOption) {
      const isAlreadyAdded = inputValue.trim() && inputValue.toLowerCase().trim() === singleValue.toLowerCase();
      if (!isAlreadyAdded) {
        const selectedLabel = generateLabelFromValue(singleValue);
        const matchesFilter =
          !query || selectedLabel.toLowerCase().includes(query) || singleValue.toLowerCase().includes(query);
        if (matchesFilter) {
          items.push({ type: 'selected-freetext', label: selectedLabel, value: singleValue, isValid: true });
        }
      }
    }

    for (const option of filteredOptions) {
      items.push({
        type: 'option',
        label: option.label,
        value: option.value,
        isValid: true,
        disabled: option.disabled,
        hint: option.hint,
        icon: option.icon
      });
    }
    return items;
  }, [
    allowCreate,
    inputValue,
    filteredOptions,
    options,
    generateLabelFromValue,
    validateValue,
    multiple,
    selectedValues,
    singleValue,
    selectedOption
  ]);

  const selectedItem = useMemo<ListItem | null>(() => {
    if (multiple || !singleValue) return null;
    return (
      displayList.find((item) => item.value.toLowerCase() === singleValue.toLowerCase()) ?? {
        type: selectedOption ? 'option' : 'selected-freetext',
        label: displayLabel ?? singleValue,
        value: singleValue,
        isValid: true
      }
    );
  }, [multiple, singleValue, displayList, selectedOption, displayLabel]);

  const selectedItems = useMemo(
    () =>
      selectedValues.map((selectedVal) => {
        const fromList = displayList.find((item) => item.value.toLowerCase() === selectedVal.toLowerCase());
        return (
          fromList ?? {
            type: 'selected-freetext' as const,
            label: getLabel(selectedVal),
            value: selectedVal,
            isValid: true
          }
        );
      }),
    [selectedValues, displayList, getLabel]
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    onOpen?.();
    setIsOpen(true);
    setInputValue('');
  }, [disabled, onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setInputValue('');
  }, []);

  const canonicalValue = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      const matchingOption = options.find((o) => o.value.toLowerCase() === trimmed.toLowerCase());
      return matchingOption ? matchingOption.value : trimmed;
    },
    [options]
  );

  const handleSingleSelect = (nextValue: string) => {
    const trimmed = nextValue.trim();
    if (!trimmed) return;
    if (validateValue && !validateValue(trimmed)) return;
    onChange(canonicalValue(trimmed));
    handleClose();
  };

  const applyMultiSelection = (nextItems: ListItem[]) => {
    const nextValues: string[] = [];
    for (const item of nextItems) {
      if (!item.isValid || item.disabled) continue;
      const trimmed = item.value.trim();
      if (!trimmed) continue;
      if (validateValue && !validateValue(trimmed)) continue;
      nextValues.push(canonicalValue(trimmed));
    }
    onChange(nextValues);
    setInputValue('');
  };

  const listedValues = filteredOptions.filter((option) => !option.disabled).map((option) => option.value);
  const allListedSelected =
    listedValues.length > 0 && listedValues.every((listed) => selectedSet.has(listed.toLowerCase()));
  const someListedSelected = !allListedSelected && listedValues.some((listed) => selectedSet.has(listed.toLowerCase()));

  const toggleAllListed = () => {
    const listedSet = new Set(listedValues.map((listed) => listed.toLowerCase()));
    onChange(
      allListedSelected
        ? selectedValues.filter((selectedVal) => !listedSet.has(selectedVal.toLowerCase()))
        : [...selectedValues, ...listedValues.filter((listed) => !selectedSet.has(listed.toLowerCase()))]
    );
  };

  const handleClear = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(multiple ? [] : null);
    setInputValue('');
  };

  return (
    <div className={twMerge('relative w-full', fontSizeClass[size], className)}>
      <BaseCombobox.Root
        items={displayList}
        filteredItems={displayList}
        multiple={multiple}
        value={multiple ? selectedItems : selectedItem}
        onValueChange={(next) => {
          if (multiple) {
            applyMultiSelection((next ?? []) as ListItem[]);
            return;
          }
          const item = next as ListItem | null;
          if (!item || !item.isValid || item.disabled) return;
          handleSingleSelect(item.value);
        }}
        inputValue={inputValue}
        onInputValueChange={(next, eventDetails) => {
          if (multiple && eventDetails.reason === 'item-press') {
            eventDetails.cancel();
            return;
          }
          setInputValue(next);
        }}
        open={isOpen}
        onOpenChange={(open, eventDetails) => {
          if (multiple && !open && eventDetails.reason === 'item-press') {
            eventDetails.cancel();
            return;
          }
          if (open) handleOpen();
          else handleClose();
        }}
        disabled={disabled}
        modal={false}
        itemToStringLabel={(item) => item.label}
        itemToStringValue={(item) => item.value}
        isItemEqualToValue={(itemValue, current) => itemValue.value.toLowerCase() === current.value.toLowerCase()}
      >
        <BaseCombobox.Trigger
          id={inputId}
          aria-label={ariaLabel}
          disabled={disabled}
          className={twMerge(
            'w-full border-2 rounded-md text-left transition-colors',
            sizeClass[size],
            multiple && selectionDisplay === 'line' && 'h-[36px] min-h-0 px-2.5 py-0 rounded-lg',
            disabled
              ? 'opacity-60 cursor-not-allowed bg-bluegray-50 border-border-default'
              : 'hover:bg-surface-hover border-border-default bg-white data-[popup-open]:border-border-active data-[popup-open]:bg-surface-hover'
          )}
        >
          {multiple ? (
            <div
              className={twMerge(
                'flex justify-between gap-2 w-full',
                selectionDisplay === 'line' ? 'items-center' : 'items-start'
              )}
            >
              {selectedValues.length > 0 && selectionDisplay === 'line' ? (
                <span className="flex-1 min-w-0 truncate text-content-default">
                  {selectedValues.map(getLabel).join(', ')}
                </span>
              ) : selectedValues.length > 0 ? (
                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1">
                  {selectedValues.map((val) => (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1 bg-gray-100 text-content-default text-xs px-2 py-0.5 rounded-md max-w-[10rem]"
                    >
                      <span className="truncate">{getLabel(val)}</span>
                      {!disabled && (
                        <span
                          role="button"
                          tabIndex={0}
                          onPointerDown={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(selectedValues.filter((v) => v.toLowerCase() !== val.toLowerCase()));
                          }}
                          onKeyDown={(e) =>
                            activateRoleButton(e, (event) => {
                              event.stopPropagation();
                              onChange(selectedValues.filter((v) => v.toLowerCase() !== val.toLowerCase()));
                            })
                          }
                          className="text-content-subtle hover:text-content-default cursor-pointer flex-shrink-0"
                          aria-label={`Remove ${getLabel(val)}`}
                        >
                          <XIcon size={12} weight="bold" />
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="flex-1 text-content-subtlest truncate">{placeholder}</span>
              )}
              <div
                className={twMerge('flex items-center gap-1 flex-shrink-0', selectionDisplay === 'chips' && 'pt-0.5')}
              >
                {selectedValues.length > 0 && !disabled && (
                  <span
                    role="button"
                    tabIndex={0}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleClear}
                    onKeyDown={(e) => activateRoleButton(e, handleClear)}
                    className="text-content-subtle hover:text-content-default cursor-pointer"
                    aria-label="Clear all"
                  >
                    <XCircleIcon size={18} weight="fill" />
                  </span>
                )}
                <CaretDownIcon
                  size={18}
                  weight="light"
                  className="text-content-subtle transition-transform [[data-popup-open]_&]:rotate-180"
                  aria-hidden="true"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 w-full">
              {singleValue ? (
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="text-content-default truncate">{displayLabel}</span>
                  {displayLabel !== singleValue && (
                    <span className="text-xs text-content-subtle truncate">{singleValue}</span>
                  )}
                </div>
              ) : (
                <span className="flex-1 text-content-subtlest truncate">{placeholder}</span>
              )}
              <div className="flex items-center gap-1 flex-shrink-0">
                {singleValue && !disabled && (
                  <span
                    role="button"
                    tabIndex={0}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleClear}
                    onKeyDown={(e) => activateRoleButton(e, handleClear)}
                    className="text-content-subtle hover:text-content-default focus:outline-none focus:ring-1 focus:ring-primary-500 rounded-full cursor-pointer"
                    aria-label="Clear selection"
                  >
                    <XCircleIcon size={18} weight="fill" />
                  </span>
                )}
                <CaretDownIcon
                  size={18}
                  weight="light"
                  className="text-content-subtle transition-transform [[data-popup-open]_&]:rotate-180"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}
        </BaseCombobox.Trigger>

        <BaseCombobox.Portal>
          <BaseCombobox.Positioner side="bottom" align="start" sideOffset={8} className="z-critical">
            <BaseCombobox.Popup
              id={listboxId}
              className={twMerge(
                'flex flex-col overflow-hidden bg-white border-[2px] border-border-default rounded-lg shadow-lg outline-none w-[var(--anchor-width)] min-w-[var(--anchor-width)]',
                maxHeight
              )}
            >
              <div className="flex-shrink-0 p-2 border-b border-border-default">
                <BaseCombobox.Input
                  placeholder={searchPlaceholder}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      // Enter confirms the highlighted option; without
                      // preventDefault the parent <form> submits instead.
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  className={twMerge(
                    'w-full px-2 py-1.5 border-2 border-border-default rounded-md bg-white',
                    'focus:border-border-active focus:outline-none',
                    'text-sm text-content-default placeholder:text-content-subtlest'
                  )}
                  aria-label="Search options"
                />
              </div>
              {multiple && enableSelectAll && listedValues.length > 0 && (
                // The padding matches the gap above the first option, so the divider sits midway between rows.
                <div className="flex-shrink-0 border-b border-border-subtle pb-1">
                  <button
                    type="button"
                    // Keep focus in the search input, so the popup stays open and typing carries on.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={toggleAllListed}
                    className={twMerge(
                      'flex w-full items-center gap-2.5 px-3 py-2 cursor-pointer text-content-default font-medium outline-none hover:bg-surface-hover',
                      fontSizeClass[size]
                    )}
                  >
                    <Checkbox
                      checked={allListedSelected}
                      indeterminate={someListedSelected}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                      className="pointer-events-none flex-shrink-0"
                    />
                    {selectAllIcon}
                    <span className="min-w-0 flex-1 text-left">{allListedSelected ? 'Clear all' : 'Select all'}</span>
                  </button>
                </div>
              )}
              <BaseCombobox.Empty className="text-content-subtlest text-center empty:p-0 px-3 py-2">
                {emptyText}
              </BaseCombobox.Empty>
              <BaseCombobox.List
                className={twMerge('min-h-0 overflow-y-auto custom-scrollbar-thin py-1', fontSizeClass[size])}
                style={{ maxHeight: '240px' }}
              >
                {(item: ListItem) => {
                  const isFreetextItem = item.type === 'freetext';
                  const isInvalid = isFreetextItem && item.isValid === false;
                  const isDisabled = isInvalid || item.disabled === true;
                  const isSelected = multiple && selectedSet.has(item.value.toLowerCase());
                  const secondaryText = item.hint
                    ? item.hint
                    : shouldShowOptionValue(item.label, item.value)
                      ? item.value
                      : null;
                  return (
                    <BaseCombobox.Item
                      key={`${item.type}-${item.value}`}
                      value={item}
                      disabled={isDisabled}
                      className={twMerge(
                        'outline-none',
                        multiple ? 'px-3 py-2 flex items-center gap-2.5' : 'px-3 py-2 flex flex-col gap-0.5',
                        isInvalid && invalidCreateTitle
                          ? 'cursor-default text-content-subtle'
                          : isDisabled
                            ? 'cursor-not-allowed opacity-60 text-content-subtle'
                            : 'cursor-pointer hover:bg-surface-hover data-[highlighted]:bg-surface-hover',
                        !multiple && 'data-[selected]:bg-surface-hover data-[selected]:font-medium'
                      )}
                    >
                      {multiple && !(isInvalid && invalidCreateTitle) && (
                        <Checkbox
                          checked={isSelected}
                          readOnly
                          disabled={isDisabled}
                          className="pointer-events-none flex-shrink-0"
                        />
                      )}
                      {isInvalid && invalidCreateTitle ? (
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-content-subtle">{invalidCreateTitle}</span>
                          <span className="text-content-subtle">{invalidCreateDescription}</span>
                        </div>
                      ) : multiple ? (
                        <>
                          {item.icon}
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span
                              className={twMerge('text-content-default truncate', isDisabled && 'text-content-subtle')}
                            >
                              {isFreetextItem && (
                                <span className="text-content-subtle italic mr-1">{createOptionLabel}</span>
                              )}
                              {item.label}
                            </span>
                            {secondaryText && (
                              <span className="text-xs text-content-subtle truncate">{secondaryText}</span>
                            )}
                            {isInvalid && (
                              <span className="text-xs text-red-600 mt-0.5">
                                {validationMessage || 'Invalid value'}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <span
                            className={twMerge(
                              'text-content-default',
                              item.icon != null && 'flex items-center gap-2.5',
                              isDisabled && 'text-content-subtle'
                            )}
                          >
                            {item.icon}
                            {isFreetextItem && (
                              <span className="text-content-subtle italic mr-1" aria-hidden="true">
                                {createOptionLabel}
                              </span>
                            )}
                            {item.label}
                          </span>
                          {secondaryText && <span className="text-xs text-content-subtle">{secondaryText}</span>}
                          {isInvalid && (
                            <span className="text-xs text-red-600 mt-0.5">{validationMessage || 'Invalid value'}</span>
                          )}
                        </>
                      )}
                    </BaseCombobox.Item>
                  );
                }}
              </BaseCombobox.List>
            </BaseCombobox.Popup>
          </BaseCombobox.Positioner>
        </BaseCombobox.Portal>
      </BaseCombobox.Root>
    </div>
  );
}

function ComboboxInner(props: ComboboxProps, ref: React.ForwardedRef<HTMLInputElement>) {
  if (props.variant === 'field') {
    return <FieldCombobox ref={ref} {...props} />;
  }
  return <ListCombobox {...props} />;
}

const Combobox = React.forwardRef(ComboboxInner) as React.ForwardRefExoticComponent<
  FieldComboboxProps & React.RefAttributes<HTMLInputElement>
> & {
  (props: MultiListComboboxProps): React.ReactElement;
  (props: SingleListComboboxProps): React.ReactElement;
  displayName?: string;
};

Combobox.displayName = 'Combobox';

export default Combobox;
